/**
 * Guardrail Insights Viewer - Report Data Hook
 *
 * @description Dynamically loads report data from multiple sources with
 *              V2/V1 API compatibility and automatic fallback.
 *
 * @copyright 2026 Guardrail Technologies - PROPRIETARY
 */

import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { sampleReport, toolDetails } from '../data/sampleReportData';
import { fetchAndTransformScan, transformScanToInsights, adaptApiResponseToStoredScan, type InsightsReport, type StoredScan } from '../utils/scanToInsights';
import type { ToolDetail } from '../components/report/ToolDetailDialog';
import { getStoredToken } from '../lib/api';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface ReportData {
  report: InsightsReport;
  toolDetails: Record<string, ToolDetail>;
}

export interface UseReportDataResult {
  data: ReportData | null;
  loading: boolean;
  error: string | null;
  isDynamic: boolean;
  scanId: string | null;
  scanStatus?: string;
  failureReason?: string;
  failureTarget?: string;
  failureDate?: string;
  refetch: () => void;
}

// Global window extensions
declare global {
  interface Window {
    GUARDRAIL_REPORT_DATA?: Record<string, unknown>;
    GUARDRAIL_API_URL?: string;
  }
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_API_URL = import.meta.env?.DEV ? 'http://localhost:32002' : '';
const LOG_PREFIX = '[useReportData]';

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get the configured API URL from window, environment, or default
 */
export function getApiUrl(): string {
  if (typeof window !== 'undefined' && window.GUARDRAIL_API_URL) {
    return window.GUARDRAIL_API_URL;
  }

  const envUrl = import.meta.env?.VITE_API_URL || import.meta.env?.VITE_GUARDRAIL_API_URL;
  if (envUrl) {
    return envUrl;
  }

  return DEFAULT_API_URL;
}

/**
 * Get raw report data from window global
 */
export function getRawReportData(): Record<string, unknown> | null {
  if (typeof window !== 'undefined' && window.GUARDRAIL_REPORT_DATA) {
    return window.GUARDRAIL_REPORT_DATA;
  }
  return null;
}

/**
 * Get AI-BOM data from injected report data
 */
export function getAiBomData(): unknown | null {
  const data = getRawReportData();
  return data?.aiBom ?? null;
}

/**
 * Check if dynamic data is available
 */
export function hasDynamicData(): boolean {
  return getRawReportData() !== null;
}

/**
 * Get plugin name from report data with fallback support
 */
export function getPluginName(): string {
  const data = getRawReportData();
  if (!data) return 'Unknown Plugin';

  // Check for flat format: pluginName
  if (typeof data.pluginName === 'string') {
    return data.pluginName;
  }

  // Check for nested format: plugin.name
  if (data.plugin && typeof data.plugin === 'object' && data.plugin !== null) {
    const plugin = data.plugin as Record<string, unknown>;
    if (typeof plugin.name === 'string') {
      return plugin.name;
    }
  }

  return 'Unknown Plugin';
}

/**
 * Build auth headers for API calls
 */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getStoredToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// =============================================================================
// API FETCHING FUNCTIONS
// =============================================================================

/**
 * Fetch scan data using a report token (secure viewing)
 * Tries V2 endpoint first, falls back to V1
 */
async function fetchWithToken(token: string): Promise<Record<string, unknown> | null> {
  const apiUrl = getApiUrl();

  // Try V2 endpoint first
  try {
    const response = await fetch(`${apiUrl}/v2/reports/view?token=${token}`, {
      headers: getAuthHeaders(),
    });
    if (response.ok) {
      const data = await response.json();
      return (data.scan || data.data) as Record<string, unknown> | null;
    }
  } catch {
    if (import.meta.env.DEV) console.log(`${LOG_PREFIX} V2 token endpoint not available, trying V1...`);
  }

  // Fallback to V1 endpoint
  const response = await fetch(`${apiUrl}/v1/report/view?token=${token}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch report');
  }
  const data = await response.json();
  return data.scan as Record<string, unknown> | null;
}

/**
 * Fetch latest scan from API
 * Tries V2 history endpoint first, falls back to V1 latest
 */
async function fetchLatestScan(apiUrl: string): Promise<Record<string, unknown> | null> {
  const headers = getAuthHeaders();
  // Try V2 history endpoint first — returns summaries, then fetch full record
  try {
    const response = await fetch(`${apiUrl}/v2/scans/history?limit=1`, { headers });
    if (response.ok) {
      const data = await response.json();
      const scans = (data.scans || data.data || []) as Record<string, unknown>[];
      if (scans.length > 0) {
        const summary = scans[0];
        const scanId = summary.scanId as string | undefined;
        // The history endpoint returns summaries (no findings/result payload).
        // Fetch the full scan record so transformScanToInsights gets everything it needs.
        if (scanId) {
          try {
            const fullResp = await fetch(`${apiUrl}/v2/scan/${scanId}`, { headers });
            if (fullResp.ok) {
              return await fullResp.json() as Record<string, unknown>;
            }
          } catch {
            if (import.meta.env.DEV) console.log(`${LOG_PREFIX} Full scan fetch failed, using summary`);
          }
        }
        return summary;
      }
    }
  } catch {
    if (import.meta.env.DEV) console.log(`${LOG_PREFIX} V2 history endpoint not available, trying V1...`);
  }

  // Fallback to V1 endpoint
  try {
    const response = await fetch(`${apiUrl}/v1/scan/latest`);
    if (response.ok) {
      const data = await response.json();
      return data.scan as Record<string, unknown> | null;
    }
  } catch {
    if (import.meta.env.DEV) console.log(`${LOG_PREFIX} V1 latest endpoint not available`);
  }

  return null;
}

/**
 * Fetch from local JSON file
 */
async function fetchLocalJson(): Promise<Record<string, unknown> | null> {
  try {
    const response = await fetch('./report-data.json');
    if (response.ok) {
      return await response.json();
    }
  } catch {
    if (import.meta.env.DEV) console.log(`${LOG_PREFIX} No report-data.json found`);
  }
  return null;
}

// =============================================================================
// REPORT CREATION
// =============================================================================

/**
 * Create empty report structure (no sample data contamination)
 */
function createEmptyReport(): InsightsReport {
  return {
    reportId: '',
    reportDate: '',
    pluginName: '',
    classification: 'Confidential',
    generatedAt: new Date(),
    scores: {
      aivss: { score: 0, level: 'low' as const },
      cvss: { score: 0, level: 'low' as const },
      aars: { score: 0, level: 'low' as const },
      // creatorTrust intentionally omitted — keep undefined when absent so
      // the optional-render guard in ReportPage ({reportData.scores.creatorTrust && ...})
      // correctly suppresses the Creator Trust card for scans that lack VendorGuard data.
      overall: 0,
    },
    scoreBreakdowns: { aivss: [], cvss: [], aars: [] },
    mcpTools: {
      totalTools: 0,
      highRiskTools: 0,
      mediumRiskTools: 0,
      lowRiskTools: 0,
      toolsList: [],
      toolsInfo: {},
    },
    vulnerabilities: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
    keyFindings: [],
    creator: {
      name: 'Unknown',
      organization: 'Unknown',
      verified: false,
      country: 'Unknown',
      organizationType: 'Unknown',
      trustScore: 0,
    },
    plugin: {
      name: '',
      description: '',
      version: '',
      location: '',
      category: '',
      license: 'Unknown',
    },
    vulnerabilityDetails: [],
    permissions: [],
    recommendation: { type: 'conditional', summary: '', conditions: [] },
    threatModeling: { threatActors: [], attackVectors: [] },
    prioritizedRemediation: [],
    riskAcceptanceItems: [],
  };
}

/**
 * Build ReportData from scan data
 */
function buildReportData(
  scanData: Record<string, unknown>,
  additionalToolDetails?: Record<string, unknown>
): ReportData {
  const reportData = (scanData.report || scanData) as Record<string, unknown>;
  const tools = (scanData.toolDetails || additionalToolDetails || {}) as Record<string, ToolDetail>;

  // Check if data needs transformation (API format vs Insights format)
  // Check if data needs transformation (API format vs Insights format).
  // The v2/scan/:id endpoint wraps findings under a 'result' key:
  //   { scanId, status, result: { findings, scores, ... } }
  // The direct scan format has findings at the top level.
  // Either shape needs transformScanToInsights — detect both.
  // Single derivation point: adapt if result-wrapped, then check for transform eligibility.
  // Both `needsTransform` and `storedScan` derive from the same `adapted` value so they
  // can never diverge (Claude finding #4).
  const adapted: StoredScan | null = (() => {
    if (!reportData.scanId) return null;
    const isResultWrapped =
      reportData.result != null &&
      typeof reportData.result === 'object' &&
      ((reportData.result as Record<string, unknown>).findings != null ||
       (reportData.result as Record<string, unknown>).scores != null);
    if (isResultWrapped) return adaptApiResponseToStoredScan(reportData);
    if (reportData.findings) return reportData as unknown as StoredScan;
    return null;
  })();

  const emptyReport = createEmptyReport();

  const report = adapted
    ? { ...emptyReport, ...transformScanToInsights(adapted) }
    : {
        ...emptyReport,
        ...reportData,
        // Deep-merge scores so missing keys (e.g. creatorTrust) fall back to
        // the empty-report defaults instead of being silently dropped when the
        // API returns a scores object that predates a new score field.
        scores: {
          ...emptyReport.scores,
          ...(reportData.scores as Record<string, unknown> | undefined),
        },
      };

  return {
    report: report as InsightsReport,
    toolDetails: { ...toolDetails, ...tools },
  };
}

/**
 * Extract scan ID from various data formats
 */
function extractScanId(data: Record<string, unknown>): string | null {
  const reportData = (data.report || data) as Record<string, unknown>;
  return (data.scanId || reportData.scanId || reportData.reportId || null) as string | null;
}

// =============================================================================
// MAIN HOOK
// =============================================================================

/**
 * Hook to load report data dynamically.
 *
 * Data loading priority:
 * 0. URL parameter ?sample=true (explicit sample report request)
 * 1. window.GUARDRAIL_REPORT_DATA (injected by parent app)
 * 2. URL parameter ?token=<token> (secure report viewing)
 * 3. URL parameter ?scan_id=<scan-id> (fetches from API)
 * 4. URL parameter ?data=<base64-encoded-json>
 * 5. Auto-fetch latest scan from API
 * 6. Local JSON file at ./report-data.json
 * 7. Sample data (fallback when no data source available)
 */
export function useReportData(): UseReportDataResult {
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchParams] = useSearchParams();

  // Read scan ID from URL reactively so the query refetches on navigation
  const urlScanId =
    searchParams.get('scanId') || searchParams.get('scan_id') || searchParams.get('id');

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Use React Query instead of useEffect for data loading
  const {
    data: result,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['report-data', urlScanId, searchParams.get('sample'), refreshKey],
    queryFn: async () => {
      // Priority 0: Explicit sample report request (?sample=true)
      if (searchParams.get('sample') === 'true') {
        if (import.meta.env.DEV) console.log(`${LOG_PREFIX} Loading sample report (explicitly requested)`);
        return {
          data: { report: sampleReport as unknown as InsightsReport, toolDetails },
          isDynamic: false,
          scanId: null,
          error: null,
        };
      }

      // Priority 1: Injected window data
      const injectedData = getRawReportData();
      if (injectedData) {
        if (import.meta.env.DEV) console.log(`${LOG_PREFIX} Loading from window.GUARDRAIL_REPORT_DATA`);
        return {
          data: buildReportData(injectedData),
          isDynamic: true,
          scanId: extractScanId(injectedData),
          error: null,
        };
      }

      // Priority 2: Token-based secure viewing
      const urlToken = searchParams.get('token');
      if (urlToken) {
        if (import.meta.env.DEV) console.log(`${LOG_PREFIX} Loading from API with token`);
        try {
          const scanData = await fetchWithToken(urlToken);
          if (scanData) {
            return {
              data: buildReportData(scanData),
              isDynamic: true,
              scanId: extractScanId(scanData),
              error: null,
            };
          }
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Token fetch failed';
          const errorMsg = msg.includes('expired')
            ? 'Report link has expired. Run open_risk_report() for a new link.'
            : msg;
          console.error(
            `${LOG_PREFIX} Token fetch failed:`,
            e instanceof Error ? e.message : 'Unknown error'
          );
          throw new Error(errorMsg, { cause: e });
        }
      }

      // Priority 3: Scan ID from URL
      if (urlScanId) {
        if (import.meta.env.DEV) console.log(`${LOG_PREFIX} Loading from API with scan_id: ${urlScanId}`);

        const apiUrl = getApiUrl();
        let fetchResult;
        try {
          fetchResult = await fetchAndTransformScan(apiUrl, urlScanId);
        } catch (e) {
          // AUTH_REQUIRED means the session expired — handleAuthFailure() already
          // scheduled the redirect. Re-throw with a user-friendly message so the
          // error UI says "session expired" rather than "could not load scan".
          if (e instanceof Error && e.message === 'AUTH_REQUIRED') {
            throw new Error('Session expired. Redirecting to login...', { cause: e });
          }
          throw e;
        }

        if (fetchResult) {
          // Handle failed scans — return failure metadata without a full report
          if (fetchResult.failed) {
            return {
              data: null,
              isDynamic: true,
              scanId: urlScanId,
              error: null,
              scanStatus: 'failed',
              failureReason: fetchResult.failureReason,
              failureTarget: fetchResult.target,
              failureDate: fetchResult.scannedAt,
            };
          }

          if (fetchResult.report) {
            return {
              data: {
                report: {
                  ...createEmptyReport(),
                  ...fetchResult.report,
                } as InsightsReport,
                toolDetails,
              },
              isDynamic: true,
              scanId: urlScanId,
              error: null,
            };
          }
        }

        console.warn(`${LOG_PREFIX} Failed to fetch scan from API`);
        throw new Error(`Could not load scan ${urlScanId} from API`);
      }

      // Priority 4: Base64 data from URL
      const base64Data = searchParams.get('data');
      if (base64Data) {
        if (import.meta.env.DEV) console.log(`${LOG_PREFIX} Loading from URL base64 parameter`);
        try {
          const jsonString = atob(base64Data);
          const urlData = JSON.parse(jsonString) as Record<string, unknown>;
          return {
            data: buildReportData(urlData),
            isDynamic: true,
            scanId: extractScanId(urlData),
            error: null,
          };
        } catch (e) {
          console.warn(
            `${LOG_PREFIX} Failed to parse URL data parameter:`,
            e instanceof Error ? e.message : 'Unknown error'
          );
        }
      }

      // Priority 5: Auto-fetch latest from API
      const apiUrl = getApiUrl();
      if (import.meta.env.DEV) console.log(`${LOG_PREFIX} Trying to fetch latest scan from API`);
      const latestScan = await fetchLatestScan(apiUrl);
      if (latestScan) {
        if (import.meta.env.DEV) console.log(`${LOG_PREFIX} Loading latest scan`);
        return {
          data: buildReportData(latestScan),
          isDynamic: true,
          scanId: extractScanId(latestScan),
          error: null,
        };
      }

      // Priority 6: Local JSON file
      const localData = await fetchLocalJson();
      if (localData) {
        if (import.meta.env.DEV) console.log(`${LOG_PREFIX} Loading from report-data.json`);
        return {
          data: buildReportData(localData),
          isDynamic: true,
          scanId: extractScanId(localData),
          error: null,
        };
      }

      // Priority 7: Fall back to sample report for the "View Sample Report" use-case
      // (user navigated to /report with no query params and no API data).
      if (import.meta.env.DEV) console.log(`${LOG_PREFIX} No dynamic data found — loading sample report`);
      return {
        data: { report: sampleReport as unknown as InsightsReport, toolDetails },
        isDynamic: false,
        scanId: null,
        error: null,
      };
    },
  });

  const error = queryError ? (queryError as Error).message : result?.error || null;

  return {
    data: result?.data || null,
    loading,
    error,
    isDynamic: result?.isDynamic || false,
    scanId: result?.scanId || null,
    scanStatus: result?.scanStatus,
    failureReason: result?.failureReason,
    failureTarget: result?.failureTarget,
    failureDate: result?.failureDate,
    refetch,
  };
}

export default useReportData;
